/**
 * What cloud discovery may do in a customer's AWS account, and what it must never be able to do.
 *
 * Both lists live here, once, because three things must agree on them: the policy the console
 * tells an administrator to attach, the check that refuses over-privileged keys, and the
 * collectors that call AWS. ADR 0005 records why.
 *
 * Discovery reads the *shape* of an estate — what exists and how it is wired — never its
 * contents: no objects, no items, no secret values, no function code, no log lines.
 */
/**
 * The only permissions discovery needs. Read-only, metadata-only. Kept deliberately narrower than
 * the AWS-managed `ReadOnlyAccess`, which also grants `s3:GetObject`, `dynamodb:Scan`,
 * `ssm:GetParameter` and `lambda:GetFunction` — reading data, configuration values and code.
 */
export const DISCOVERY_PERMISSIONS = [
    {
        actions: ['sts:GetCallerIdentity'],
        purpose: 'Confirm which account and user the key belongs to',
    },
    {
        actions: ['route53:ListHostedZones', 'route53:ListResourceRecordSets'],
        purpose: 'Map domain names to the CloudFront, load balancer or API they point at',
    },
    {
        actions: ['acm:ListCertificates', 'acm:DescribeCertificate'],
        purpose: 'TLS certificates and the names they cover',
    },
    {
        actions: ['cloudfront:ListDistributions', 'cloudfront:GetDistributionConfig'],
        purpose: 'Distributions, their origins and the WAF protecting them',
    },
    {
        actions: ['wafv2:ListWebACLs', 'wafv2:GetWebACL', 'wafv2:ListResourcesForWebACL'],
        purpose: 'Which resources a web ACL protects',
    },
    { actions: ['apigateway:GET'], purpose: 'APIs, routes, integrations and authorizers' },
    {
        actions: ['cognito-idp:ListUserPools', 'cognito-idp:DescribeUserPool'],
        purpose: 'User pools that APIs authenticate against (never users)',
    },
    {
        actions: [
            'elasticloadbalancing:DescribeLoadBalancers',
            'elasticloadbalancing:DescribeListeners',
            'elasticloadbalancing:DescribeRules',
            'elasticloadbalancing:DescribeTargetGroups',
            'elasticloadbalancing:DescribeTargetHealth',
            'elasticloadbalancing:DescribeTags',
        ],
        purpose: 'Load balancers, listeners and what they route to',
    },
    {
        actions: [
            'ecs:ListClusters',
            'ecs:DescribeClusters',
            'ecs:ListServices',
            'ecs:DescribeServices',
            'ecs:DescribeTaskDefinition',
        ],
        purpose: 'Container services, their network placement and environment variable names',
    },
    {
        actions: [
            'lambda:ListFunctions',
            'lambda:ListEventSourceMappings',
            'lambda:GetFunctionUrlConfig',
            'lambda:GetPolicy',
            'lambda:ListTags',
        ],
        purpose: 'Functions, what triggers them and who may invoke them (never their code)',
    },
    {
        actions: ['sqs:ListQueues', 'sqs:GetQueueAttributes'],
        purpose: 'Queues and their encryption (never messages)',
    },
    {
        actions: ['sns:ListTopics', 'sns:ListSubscriptions', 'sns:GetTopicAttributes'],
        purpose: 'Topics and their subscribers',
    },
    {
        actions: ['events:ListEventBuses', 'events:ListRules', 'events:ListTargetsByRule'],
        purpose: 'Event rules and their targets',
    },
    { actions: ['states:ListStateMachines'], purpose: 'Step Functions workflows' },
    {
        actions: ['dynamodb:ListTables', 'dynamodb:DescribeTable'],
        purpose: 'Tables and their encryption (never items)',
    },
    {
        actions: ['rds:DescribeDBClusters', 'rds:DescribeDBInstances'],
        purpose: 'Databases, their exposure and encryption',
    },
    {
        actions: ['elasticache:DescribeCacheClusters', 'elasticache:DescribeReplicationGroups'],
        purpose: 'Caches and their encryption',
    },
    {
        actions: [
            's3:ListAllMyBuckets',
            's3:GetBucketLocation',
            's3:GetBucketPublicAccessBlock',
            's3:GetEncryptionConfiguration',
            's3:GetBucketPolicyStatus',
            's3:GetBucketTagging',
        ],
        purpose: 'Buckets, whether they are public, and their encryption (never objects)',
    },
    {
        actions: ['secretsmanager:ListSecrets'],
        purpose: 'Which secrets exist, by name (never values)',
    },
    { actions: ['kms:ListAliases'], purpose: 'Key names, to label encryption' },
    {
        actions: ['ec2:DescribeVpcs', 'ec2:DescribeSubnets', 'ec2:DescribeSecurityGroups'],
        purpose: 'Networks and which security groups can reach which',
    },
    {
        actions: [
            'iam:GetRole',
            'iam:ListRolePolicies',
            'iam:GetRolePolicy',
            'iam:ListAttachedRolePolicies',
            'iam:GetPolicy',
            'iam:GetPolicyVersion',
        ],
        purpose: 'What each workload role may access — the blast radius if it is compromised',
    },
    {
        actions: [
            'cloudformation:ListStacks',
            'cloudformation:DescribeStacks',
            'cloudformation:ListStackResources',
        ],
        purpose: 'Which stack each resource belongs to',
    },
    { actions: ['tag:GetResources'], purpose: 'Resource tags' },
];
/**
 * Permissions on the key's own IAM user, so the platform can prove the key is no more powerful
 * than this policy. Scoped to `${aws:username}`: the key can inspect itself and nobody else.
 */
export const SELF_INSPECTION_ACTIONS = [
    'iam:SimulatePrincipalPolicy',
    'iam:ListAccessKeys',
    'iam:GetUser',
];
/** Every discovery action, flattened. */
export const DISCOVERY_ACTIONS = DISCOVERY_PERMISSIONS.flatMap((p) => p.actions);
/**
 * The policy an administrator attaches to the discovery user. Shown in the console to copy, and
 * kept in `docs/runbooks/cloud-discovery-policy.json`; a test pins the two together.
 */
export function discoveryPolicyDocument() {
    return {
        Version: '2012-10-17',
        Statement: [
            {
                Sid: 'GuardianDiscoveryReadMetadata',
                Effect: 'Allow',
                Action: [...DISCOVERY_ACTIONS],
                Resource: '*',
            },
            {
                Sid: 'GuardianDiscoveryInspectSelf',
                Effect: 'Allow',
                Action: [...SELF_INSPECTION_ACTIONS],
                Resource: 'arn:aws:iam::*:user/${aws:username}',
            },
        ],
    };
}
/**
 * Actions a discovery key must **not** be able to perform. If simulation says the key may do any
 * of them, the connection is refused: these read data, change the estate, or escalate privilege,
 * and a key that can is not a discovery key — however it was labelled.
 *
 * A sample, not an exhaustive list of everything harmful: it is chosen so that any broad grant
 * (`*`, `AdministratorAccess`, `PowerUserAccess`, `ReadOnlyAccess`, a service-wide `s3:*`) trips it.
 * Simulation runs against resource `*`, so a grant scoped to one named resource can pass; the
 * shipped policy is still the only thing the console asks for.
 */
export const SENSITIVE_ACTIONS = [
    // Privilege escalation and identity
    'iam:CreateUser',
    'iam:CreateAccessKey',
    'iam:AttachUserPolicy',
    'iam:PutUserPolicy',
    'iam:CreateRole',
    'iam:AttachRolePolicy',
    'iam:PutRolePolicy',
    'iam:UpdateAssumeRolePolicy',
    'iam:PassRole',
    'sts:AssumeRole',
    // Reading data
    's3:GetObject',
    'dynamodb:Scan',
    'dynamodb:Query',
    'dynamodb:GetItem',
    'secretsmanager:GetSecretValue',
    'ssm:GetParameter',
    'ssm:GetParameters',
    'kms:Decrypt',
    'lambda:GetFunction',
    'logs:GetLogEvents',
    'sqs:ReceiveMessage',
    'cognito-idp:ListUsers',
    'ecr:GetDownloadUrlForLayer',
    // Changing the estate
    's3:PutObject',
    's3:DeleteObject',
    's3:PutBucketPolicy',
    'dynamodb:PutItem',
    'lambda:UpdateFunctionCode',
    'lambda:InvokeFunction',
    'ec2:RunInstances',
    'ec2:AuthorizeSecurityGroupIngress',
    'ec2:TerminateInstances',
    'rds:DeleteDBCluster',
    'cloudformation:CreateStack',
    'cloudformation:DeleteStack',
    'ssm:SendCommand',
    'sns:Publish',
];
//# sourceMappingURL=cloud-discovery.js.map